import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  IndianRupee,
  Pencil,
  Plus,
  Save,
  Search,
  Tag,
  FileText,
  ToggleLeft,
  Trash2,
  X,
} from "lucide-react";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "./documentSharedStyles";
import {
  usePortaledActionMenu,
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackChip,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  EntriesControl,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  ModalOverlay,
  ModalShell,
  ModalHero,
  ModalHeroGlow,
  ModalHeroRow,
  ModalHeroText,
  ModalHeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
  FieldGrid,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldTextArea,
  FieldSelect,
  ModalActions,
  SecondaryButton,
  PrimaryButton,
  StatusConfirmOverlay,
  StatusConfirmBox,
  StatusBody,
  StatusCancelBtn,
  StatusConfirmBtn,
} from "./quotationMasterListShared";
import { SubmitButton, FormLabelIcon, FormGradientDefs } from "../../molecules/Dashboard/Member/memberFormStyles";
import ExportDropdown from "../../atoms/Dashboard/ExportDropdown";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import { formatINR } from "../../../Utils/helper";
import { useAppSelector } from "../../../Redux/Hooks";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { ListReportPrintTemplate, ReportStatusBadge, CellBold } from "./ListReportPrintTemplate";
import {
  getFixChargesAPI,
  createFixChargeAPI,
  updateFixChargeAPI,
  deleteFixChargeAPI,
} from "../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
  ClickableStatusPill,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import { getApiErrorMessage } from "../../atoms/Utlis/getApiErrorMessage";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";

const ScrollPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const ScrollPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const ScrollTable = styled(PanelTableScroll)`
  flex: none;
  overflow-y: visible;
  min-height: unset;
`;

type FixCharge = {
  id: number;
  charge_name: string;
  description: string;
  default_price: string | number;
  status?: string;
  created_at: string;
  branch_id?: number | null;
  branch_name?: string | null;
};

const QuotationFixChargesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, branches, selectedBranch } = useAppSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("quotation.update");
  const canDelete = hasPermission("quotation.delete");

  const reportPrintRef = useRef<HTMLDivElement>(null);
  const [chargesList, setChargesList] = useState<FixCharge[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<FixCharge | null>(null);
  const {
    activeMenuId,
    menuCoords,
    menuButtonRefs,
    scrollRef,
    handleToggleMenu,
    closeMenu,
  } = usePortaledActionMenu(140);
  const [statusConfirmCharge, setStatusConfirmCharge] = useState<FixCharge | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [branchId, setBranchId] = useState<string | number>(selectedBranch?.id || "");
  const [chargeName, setChargeName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultPrice, setDefaultPrice] = useState<number | "">(0);
  const [status, setStatus] = useState("active");

  const [totalCount, setTotalCount] = useState(0);

  const fetchCharges = async () => {
    setLoading(true);
    try {
      const res = await getFixChargesAPI({
        page: currentPage,
        page_size: entries,
        search: searchText,
        branch_id: selectedBranch?.id
      });
      setChargesList(res?.results || []);
      setTotalCount(res?.count || 0);
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to fetch fixed charges"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCharges();
  }, [currentPage, entries, searchText, selectedBranch?.id]);

  const openAddModal = () => {
    setEditingCharge(null);
    setBranchId(selectedBranch?.id || "");
    setChargeName("");
    setDescription("");
    setDefaultPrice(0);
    setStatus("active");
    setIsAddModalOpen(true);
  };

  const openEditModal = (charge: FixCharge) => {
    setEditingCharge(charge);
    setBranchId(charge.branch_id || "");
    setChargeName(charge.charge_name);
    setDescription(charge.description || "");
    setDefaultPrice(Number(charge.default_price) || 0);
    setStatus(charge.status || "active");
    setIsAddModalOpen(true);
  };

  const handleSaveCharge = async () => {
    if (isSaving) return;
    if (!chargeName.trim()) {
      showErrorToast("Charge name is required");
      return;
    }
    setIsSaving(true);
    const data = {
      charge_name: chargeName,
      description,
      default_price: Number(defaultPrice) || 0,
      status,
      branch_id: branchId ? Number(branchId) : null,
    };

    try {
      if (editingCharge) {
        await updateFixChargeAPI(editingCharge.id, data);
        showSuccessToast("Fix charge updated successfully!");
      } else {
        await createFixChargeAPI(data);
        showSuccessToast("Fix charge created successfully!");
      }
      setIsAddModalOpen(false);
      fetchCharges();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to save fix charge"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId === null || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteFixChargeAPI(deleteConfirmId);
      showSuccessToast("Fix charge deleted successfully!");
      fetchCharges();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to delete fix charge"));
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const handleCopy = () => {
    const mapped = chargesList.map((c) => ({
      ...c,
      priceWithSymbol: `₹${c.default_price}`,
    }));
    copyDataToClipboard(
      mapped,
      ["ID", "Charge Name", "Price", "Status"],
      ["id", "charge_name", "priceWithSymbol", "status"],
      "Fix charge list copied to clipboard"
    );
  };

  const handleExportCSV = () => {
    exportDataToCSV(
      chargesList,
      ["ID", "Charge Name", "Price", "Status", "Created At"],
      ["id", "charge_name", "default_price", "status", "created_at"],
      "fixed_charges_export.csv"
    );
  };

  const handleExportExcel = () => {
    exportDataToExcel(
      chargesList,
      ["ID", "Charge Name", "Price", "Status", "Created At"],
      ["id", "charge_name", "default_price", "status", "created_at"],
      "fixed_charges_export.xls"
    );
  };

  const chargeReportRows = useMemo(
    () =>
      chargesList.map((c) => ({
        charge_name: c.charge_name,
        default_price: `₹${formatINR(c.default_price)}`,
        price_raw: Number(c.default_price) || 0,
        status: c.status === "inactive" ? "Inactive" : "Active",
        status_key: c.status || "active",
        created_at: c.created_at
          ? new Date(c.created_at).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—",
      })),
    [chargesList]
  );

  const chargeReportSummary = useMemo(() => {
    const activeCount = chargeReportRows.filter((r) => r.status_key !== "inactive").length;
    const inactiveCount = chargeReportRows.length - activeCount;
    const totalValue = chargeReportRows.reduce((sum, row) => sum + row.price_raw, 0);
    return [
      { label: "Total Charges", value: String(chargeReportRows.length) },
      { label: "Active", value: String(activeCount) },
      { label: "Inactive", value: String(inactiveCount) },
      { label: "Total Charge Value", value: `₹${formatINR(totalValue)}`, highlight: true },
    ];
  }, [chargeReportRows]);

  const getReportStatusStyle = (status: string) => {
    if (status === "inactive") {
      return { bg: "rgba(23, 23, 23, 0.1)", color: "rgba(23, 23, 23, 0.65)" };
    }
    return { bg: "rgba(49, 166, 106, 0.15)", color: "#2d8a55" };
  };

  const handleExportPDF = async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fixed_charges_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch (err) {
      showErrorToast("Failed to generate PDF");
    }
  };

  const visibleCharges = chargesList;
  const totalFilteredCount = totalCount;
  const totalPages = Math.ceil(totalFilteredCount / entries) || 1;

  return (
    <ScrollPage>
      {isAddModalOpen &&
        createPortal(
          <>
            <FormGradientDefs />
            <ModalOverlay onClick={() => setIsAddModalOpen(false)}>
              <ModalShell style={{ maxWidth: 520 }} onClick={(event) => event.stopPropagation()}>
                <ModalHero>
                  <ModalHeroGlow />
                  <ModalHeroRow>
                    <ModalHeroText>
                      <ModalHeroBadge>
                        {editingCharge ? <Pencil size={11} /> : <Plus size={11} />}
                        {editingCharge ? "Edit fix charge" : "New fix charge"}
                      </ModalHeroBadge>
                      <ModalTitle>{editingCharge ? "Edit Fix Charge" : "Add Fix Charge"}</ModalTitle>
                      <ModalSubtitle>
                        {editingCharge
                          ? "Update charge details and pricing."
                          : "Create static quotation charge entry."}
                      </ModalSubtitle>
                    </ModalHeroText>
                    <CloseBtn type="button" onClick={() => setIsAddModalOpen(false)} aria-label="Close">
                      <X size={18} />
                    </CloseBtn>
                  </ModalHeroRow>
                </ModalHero>

                <ModalBody>
                  <FormCard>
                    {branches && branches.length > 0 && (
                      <FieldGroup>
                        <FieldLabel>
                          Branch (Optional)
                        </FieldLabel>
                        <FieldSelect value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                          <option value="">All Branches / Main Studio (HQ)</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.branch_name} {b.city ? `(${b.city})` : ''}
                            </option>
                          ))}
                        </FieldSelect>
                      </FieldGroup>
                    )}
                    <FieldGrid>
                      <FieldGroup>
                        <FieldLabel>
                          <FormLabelIcon icon={Tag} />
                          Charge Name
                        </FieldLabel>
                        <FieldInput
                          placeholder="Enter charge name"
                          value={chargeName}
                          onChange={(e) => setChargeName(e.target.value)}
                        />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>
                          <FormLabelIcon icon={IndianRupee} />
                          Default Price (₹)
                        </FieldLabel>
                        <FieldInput
                          type="number"
                          placeholder="Enter default price"
                          value={defaultPrice}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDefaultPrice(val === "" ? "" : Math.max(0, Number(val)));
                          }}
                        />
                      </FieldGroup>
                    </FieldGrid>
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={FileText} />
                        Description
                      </FieldLabel>
                      <FieldTextArea
                        placeholder="Enter charge description"
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={ToggleLeft} />
                        Status
                      </FieldLabel>
                      <FieldSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </FieldSelect>
                    </FieldGroup>
                  </FormCard>
                </ModalBody>

                <ModalActions>
                  <PrimaryButton type="button" onClick={handleSaveCharge} disabled={isSaving}>
                    <Save size={16} />
                    {isSaving ? "Saving..." : editingCharge ? "Update Fix Charge" : "Save Fix Charge"}
                  </PrimaryButton>
                  <SecondaryButton type="button" onClick={() => setIsAddModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                </ModalActions>
              </ModalShell>
            </ModalOverlay>
          </>,
          document.body
        )}

      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackChip type="button" onClick={() => navigate("/dashboard/quotation/")} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackChip>
            <HeroText>
              <HeroBadge>
                <IndianRupee size={11} />
                Quotation masters
              </HeroBadge>
              <PageTitle className="linear-text">All Fix Charges</PageTitle>
              <PageSubtitle>Manage quotation charge masters and service fees.</PageSubtitle>
            </HeroText>
          </HeroLeft>
          <PermissionGate permission="quotation.create">
            <SubmitButton type="button" $ready onClick={openAddModal} style={{ minWidth: 180, height: 46 }}>
              <Plus size={16} /> Add Fix Charge
            </SubmitButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <ScrollPanel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                value={searchVal}
                onChange={(event) => setSearchVal(event.target.value)}
                placeholder="Search fix charges"
              />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <select value={entries} onChange={(event) => { setEntries(Number(event.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>

        <ScrollTable ref={scrollRef}>
          <TableWrap>
            <PaymentsTable $minWidth={860}>
            <thead>
              <tr>
                <th><span>Charge Name</span></th>
                <th><span>Branch</span></th>
                <th><span>Description</span></th>
                <th><span>Default Price (₹)</span></th>
                <th><span>Status</span></th>
                <th><span>Created Date</span></th>
                <th><span>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: "30px" }}>
                    Loading fixed charges...
                  </td>
                </tr>
              ) : visibleCharges.length > 0 ? (
                visibleCharges.map((charge) => (
                  <tr key={charge.id}>
                    <td>{charge.charge_name}</td>
                    <td>
                      {charge.branch_name ? (
                        <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                          {charge.branch_name}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                    <td>{charge.description || "-"}</td>
                    <td>
                      <AmountCell>
                        ₹{Number(charge.default_price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </AmountCell>
                    </td>
                    <td>
                      <ClickableStatusPill
                        type="button"
                        $status={charge.status || "active"}
                        onClick={canUpdate ? () => setStatusConfirmCharge(charge) : undefined}
                        style={{ cursor: canUpdate ? "pointer" : "default" }}
                      >
                        {charge.status === "inactive" ? "Inactive" : "Active"}
                      </ClickableStatusPill>
                    </td>
                    <td>{charge.created_at ? new Date(charge.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "-"}</td>
                    <td>
                      {(canUpdate || canDelete) && (
                        <MenuContainer>
                          <ThreeDotsButton
                            ref={(el) => { menuButtonRefs.current[charge.id] = el; }}
                            type="button"
                            aria-expanded={activeMenuId === charge.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleMenu(charge.id);
                            }}
                          >
                            <Dot />
                            <Dot />
                            <Dot />
                          </ThreeDotsButton>
                        </MenuContainer>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "30px" }}>
                    No fixed charges found.
                  </td>
                </tr>
              )}
            </tbody>
            </PaymentsTable>
          </TableWrap>
        </ScrollTable>

        <FooterRow>
          <FooterText>
            Showing {totalFilteredCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalFilteredCount)} of {totalFilteredCount} entries
          </FooterText>
          <Pagination>
            <PageButton
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </PageButton>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <PageButton
                key={idx}
                $active={currentPage === idx + 1}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </PageButton>
            ))}
            <PageButton
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </PageButton>
          </Pagination>
        </FooterRow>
      </ScrollPanel>

      {statusConfirmCharge !== null &&
        createPortal(
          <>
            <FormGradientDefs />
            <StatusConfirmOverlay onClick={() => setStatusConfirmCharge(null)}>
              <StatusConfirmBox onClick={(e) => e.stopPropagation()}>
                <ModalHero>
                  <ModalHeroGlow />
                  <ModalHeroRow>
                    <ModalHeroText>
                      <ModalHeroBadge>
                        <ToggleLeft size={11} /> Status change
                      </ModalHeroBadge>
                      <ModalTitle>Change Status?</ModalTitle>
                      <ModalSubtitle>Update the active state for this charge.</ModalSubtitle>
                    </ModalHeroText>
                    <CloseBtn
                      type="button"
                      onClick={() => setStatusConfirmCharge(null)}
                      aria-label="Close"
                    >
                      <X size={18} />
                    </CloseBtn>
                  </ModalHeroRow>
                </ModalHero>
                <ModalBody>
                  <StatusBody>
                    Are you sure you want to change status to{" "}
                    <strong>{statusConfirmCharge.status === "active" ? "Inactive" : "Active"}</strong>?
                  </StatusBody>
                </ModalBody>
                <ModalActions>
                  <StatusConfirmBtn
                    onClick={async () => {
                      if (!statusConfirmCharge) return;
                      const newStatus =
                        statusConfirmCharge.status === "active" ? "inactive" : "active";
                      try {
                        await updateFixChargeAPI(statusConfirmCharge.id, {
                          charge_name: statusConfirmCharge.charge_name,
                          description: statusConfirmCharge.description,
                          default_price: statusConfirmCharge.default_price,
                          status: newStatus,
                        });
                        showSuccessToast(`Charge status updated to ${newStatus}!`);
                        fetchCharges();
                      } catch (err) {
                        showErrorToast("Failed to update status");
                      } finally {
                        setStatusConfirmCharge(null);
                      }
                    }}
                  >
                    Change
                  </StatusConfirmBtn>
                  <StatusCancelBtn onClick={() => setStatusConfirmCharge(null)}>
                    Cancel
                  </StatusCancelBtn>
                </ModalActions>
              </StatusConfirmBox>
            </StatusConfirmOverlay>
          </>,
          document.body
        )}

      {activeMenuId !== null && menuCoords && (() => {
        const charge = visibleCharges.find((c) => c.id === activeMenuId);
        if (!charge) return null;
        return createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {canUpdate && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    openEditModal(charge);
                  }}
                >
                  <ItemIconContainer>
                    <Pencil size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Edit</ItemLabel>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    setDeleteConfirmId(charge.id);
                  }}
                >
                  <ItemIconContainer>
                    <Trash2 size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Delete</ItemLabel>
                </MenuItem>
              )}
            </DropdownMenu>
          </>,
          document.body
        );
      })()}

      {deleteConfirmId !== null && (
        <DeletePopup
          text="Are you sure you want to delete this fix charge?"
          buttonText="Delete"
          cancel={() => setDeleteConfirmId(null)}
          Delete={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Fixed Charges Report"
        subtitle="Quotation fixed charges overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: "charge_name", label: "Charge Name", width: "32%" },
          {
            key: "default_price",
            label: "Price",
            align: "right",
            width: "16%",
            render: (value) => <CellBold>{String(value)}</CellBold>,
          },
          {
            key: "status",
            label: "Status",
            align: "center",
            width: "14%",
            render: (value, row) => {
              const style = getReportStatusStyle(String(row.status_key || ""));
              return (
                <ReportStatusBadge $bg={style.bg} $color={style.color}>
                  {String(value)}
                </ReportStatusBadge>
              );
            },
          },
          { key: "created_at", label: "Created On", width: "18%" },
        ]}
        rows={chargeReportRows}
        summaryItems={chargeReportSummary}
      />
    </ScrollPage>
  );
};

export default QuotationFixChargesPage;
